import { ReactNode, useEffect, useState } from 'react'
import cn from 'utils/cn'
import { AnimatePresence, motion } from 'framer-motion'

export interface TabItem {
  key: string
  label: ReactNode
  disabled?: boolean
  hide?: boolean
  content?: ReactNode
}

export interface TabsProps {
  /**
   * Key of the currently active tab.
   */
  activeTab: string
  /**
   * Array of tab items to display.
   */
  items: TabItem[]
  /**
   * Callback fired when the active tab changes.
   */
  onChange?: (key: TabItem['key']) => void
  /**
   * Additional class names for the tabs container.
   */
  className?: string
  /**
   * Whether to center the tabs horizontally.
   */
  centered?: boolean
}

/**
 * Tabs component for displaying a set of tabbed navigation items and their content.
 * Supports custom tab labels, disabled/hidden tabs, and animated transitions.
 */
const Tabs = ({
  activeTab,
  items = [],
  onChange,
  className,
  centered
}: TabsProps) => {
  const [defaultKey, setDefault] = useState('')

  useEffect(() => {
    const firstKey = items?.[0]?.key
    setDefault(!activeTab && firstKey ? firstKey : '')
  }, [activeTab, items])

  return (
    <div className={cn([className, centered && 'flex justify-center'])}>
      <div
        className={cn([
          'flex flex-row gap-6 overflow-auto overflow-y-hidden pb-1 border-b border-dark-2'
        ])}
      >
        {items?.map(({ key, label, disabled, hide }, idx) => {
          const isActive = key === activeTab || defaultKey === key

          if (hide) return null
          return (
            <motion.div
              data-testid={`tab-${key}`}
              transition={{ duration: 0.2 }}
              key={`${key}_${idx}`}
              className={cn([
                'text-lg font-medium whitespace-nowrap relative select-none',
                isActive ? 'text-primary' : 'text-dark-3',
                disabled
                  ? 'cursor-not-allowed'
                  : isActive
                    ? 'cursor-default'
                    : 'cursor-pointer'
              ])}
              onClick={() => !disabled && onChange?.(key)}
            >
              <span className={cn([!disabled && 'hover:opacity-75'])}>
                {label}
              </span>
              {isActive ? (
                <motion.div
                  className="absolute h-1 bg-primary top-7 left-0 right-0 bottom-0"
                  layoutId="underline"
                />
              ) : null}
            </motion.div>
          )
        })}
      </div>

      <div className="pt-3" />
      <AnimatePresence mode="wait">
        {items?.map(({ key, content }, idx) => {
          const isActive = key === activeTab || defaultKey === key
          return (
            isActive && (
              <motion.div
                key={`${key}_content_${idx}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {content}
              </motion.div>
            )
          )
        })}
      </AnimatePresence>
    </div>
  )
}

export default Tabs
